import { useEffect, useMemo, useState } from 'react';
import { Header } from '@/components/Header';
import { useAppStore } from '@/stores/appStore';
import {
  listInstantlyMetricsAsAdmin,
  type InstantlyCampaignMetric,
  type InstantlyDayMetric,
  type InstantlyPositiveReply,
  type InstantlySenderMetric,
} from '@/services/admin';
import { getAccountSettings } from '@/services/account';
import { listInstantlyCampaignOptions, type InstantlyCampaignOption } from '@/services/research';

function normalizeCampaignIds(input: unknown): string[] {
  if (!Array.isArray(input)) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input) {
    const id = typeof raw === 'string' ? raw.trim() : '';
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function EmailData() {
  const userEmail = useAppStore((s) => s.userEmail);
  const adminToken = useAppStore((s) => s.adminToken);

  const [accountLoading, setAccountLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [assignedCampaignIds, setAssignedCampaignIds] = useState<string[]>([]);

  const [dataLoading, setDataLoading] = useState(false);
  const [campaignOptions, setCampaignOptions] = useState<InstantlyCampaignOption[]>([]);
  const [filterCampaignId, setFilterCampaignId] = useState('');
  const [filterSenderEmail, setFilterSenderEmail] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [limit, setLimit] = useState('20000');
  const [campaignRows, setCampaignRows] = useState<InstantlyCampaignMetric[]>([]);
  const [senderRows, setSenderRows] = useState<InstantlySenderMetric[]>([]);
  const [dayRows, setDayRows] = useState<InstantlyDayMetric[]>([]);
  const [positiveReplies, setPositiveReplies] = useState<InstantlyPositiveReply[]>([]);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [uniqueTotals, setUniqueTotals] = useState<Record<string, number>>({});

  const assignedCampaignIdsKey = useMemo(() => assignedCampaignIds.join('|'), [assignedCampaignIds]);
  const hasAssignments = assignedCampaignIds.length > 0;

  useEffect(() => {
    const run = async () => {
      if (!userEmail || !adminToken) return;
      setAccountLoading(true);
      setStatus(null);
      try {
        const result = await getAccountSettings({ token: adminToken, email: userEmail });
        if (!result.success || !result.account) throw new Error(result.error || 'Failed to load account settings');
        setAssignedCampaignIds(normalizeCampaignIds(result.account.instantlyCampaignIds));
      } catch (error: any) {
        setStatus(error?.message || 'Failed to load assigned campaigns.');
      } finally {
        setAccountLoading(false);
      }
    };
    void run();
  }, [adminToken, userEmail]);

  useEffect(() => {
    if (!hasAssignments) {
      setCampaignOptions([]);
      setFilterCampaignId('');
      return;
    }
    let cancelled = false;
    listInstantlyCampaignOptions({ userEmail })
      .then((result) => {
        if (cancelled) return;
        const allCampaigns = Array.isArray(result.campaigns) ? result.campaigns : [];
        const byId = new Map(allCampaigns.map((campaign) => [campaign.id, campaign]));
        const restricted = assignedCampaignIds.map((id) => byId.get(id) ?? { id, name: id });
        setCampaignOptions(restricted);
        setFilterCampaignId((current) =>
          current && restricted.some((campaign) => campaign.id === current)
            ? current
            : (restricted[0]?.id ?? '')
        );
      })
      .catch(() => {
        if (cancelled) return;
        const fallback = assignedCampaignIds.map((id) => ({ id, name: id }));
        setCampaignOptions(fallback);
        setFilterCampaignId((current) =>
          current && fallback.some((campaign) => campaign.id === current)
            ? current
            : (fallback[0]?.id ?? '')
        );
      });
    return () => {
      cancelled = true;
    };
  }, [assignedCampaignIds, assignedCampaignIdsKey, hasAssignments, userEmail]);

  const loadEmailData = async () => {
    if (!userEmail || !adminToken || !hasAssignments) return;
    const selectedCampaignId = filterCampaignId.trim();
    if (!selectedCampaignId) {
      setStatus('Select an assigned campaign first.');
      return;
    }
    if (!assignedCampaignIds.includes(selectedCampaignId)) {
      setStatus('Selected campaign is not assigned to your account.');
      return;
    }
    setDataLoading(true);
    setStatus(null);
    try {
      const result = await listInstantlyMetricsAsAdmin({
        requesterEmail: userEmail.trim(),
        token: adminToken,
        userEmail: userEmail.trim(),
        campaignId: selectedCampaignId,
        senderEmail: filterSenderEmail.trim() || undefined,
        start: filterStart ? new Date(filterStart).toISOString() : undefined,
        end: filterEnd ? new Date(`${filterEnd}T23:59:59.999Z`).toISOString() : undefined,
        limit: Number(limit) || 20000,
      });
      if (!result.success) throw new Error(result.error || 'Failed to load Instantly metrics.');
      setCampaignRows(Array.isArray(result.byCampaign) ? result.byCampaign : []);
      setSenderRows(Array.isArray(result.bySender) ? result.bySender : []);
      setDayRows(Array.isArray(result.byDay) ? result.byDay : []);
      setPositiveReplies(Array.isArray(result.positiveReplies) ? result.positiveReplies : []);
      setTotals((result.totals as Record<string, number>) ?? {});
      setUniqueTotals((result.uniqueTotals as Record<string, number>) ?? {});
      setStatus(`Loaded ${(result.sampleSize ?? 0).toLocaleString()} events for campaign ${selectedCampaignId}.`);
    } catch (error: any) {
      setStatus(error?.message || 'Failed to load Instantly metrics.');
    } finally {
      setDataLoading(false);
    }
  };

  const topCampaigns = useMemo(
    () => [...campaignRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [campaignRows]
  );
  const topSenders = useMemo(
    () => [...senderRows].sort((a, b) => Number(b.total ?? 0) - Number(a.total ?? 0)).slice(0, 12),
    [senderRows]
  );
  const openRate = useMemo(() => {
    const sends = Number(totals.sends ?? 0);
    const opens = Number(totals.opens ?? 0);
    return sends > 0 ? (opens / sends) * 100 : 0;
  }, [totals.opens, totals.sends]);
  const clickRate = useMemo(() => {
    const sends = Number(totals.sends ?? 0);
    const clicks = Number(totals.clicks ?? 0);
    return sends > 0 ? (clicks / sends) * 100 : 0;
  }, [totals.clicks, totals.sends]);
  const replyRate = useMemo(() => {
    const sends = Number(totals.sends ?? 0);
    const replies = Number(totals.replies ?? 0);
    return sends > 0 ? (replies / sends) * 100 : 0;
  }, [totals.replies, totals.sends]);

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      <Header />
      <main className="max-w-6xl mx-auto px-6 py-10 space-y-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Email Data</h1>
          <p className="text-gray-400 mt-2">Instantly campaign analytics limited to assignments from Command Center.</p>
        </div>

        {status && <div className="text-xs text-gray-300">{status}</div>}

        <section className="bg-card-dark border border-border-dark rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold">Instantly Email Data</h3>
              <p className="text-xs text-gray-500">
                Campaign options are restricted to what your account was assigned in Command Center.
              </p>
            </div>
            <div className="text-xs text-gray-400">
              Assigned campaigns: <span className="text-white font-semibold">{assignedCampaignIds.length}</span>
            </div>
          </div>

          {!accountLoading && !hasAssignments && (
            <div className="rounded border border-amber-700 bg-amber-900/20 px-3 py-2 text-xs text-amber-200">
              No campaigns are assigned to your account yet. Ask an admin to assign at least one campaign in Command Center.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select
              value={filterCampaignId}
              onChange={(e) => setFilterCampaignId(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={!hasAssignments}
            >
              {campaignOptions.map((campaign) => (
                <option key={`email-data-campaign-${campaign.id}`} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
            <input
              value={filterSenderEmail}
              onChange={(e) => setFilterSenderEmail(e.target.value)}
              placeholder="Sender Email (optional)"
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={!hasAssignments}
            />
            <input
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="Limit (max 50000)"
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={!hasAssignments}
            />
            <input
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={!hasAssignments}
            />
            <input
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white"
              disabled={!hasAssignments}
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                void loadEmailData();
              }}
              disabled={dataLoading || !hasAssignments || !filterCampaignId}
              className="px-3 py-1 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 text-xs font-semibold"
            >
              {dataLoading ? 'Loading…' : 'Run Report'}
            </button>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-gray-400">Sends</div>
            <div className="text-white text-base font-semibold">{Number(totals.sends ?? 0).toLocaleString()}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-gray-400">Opens</div>
            <div className="text-white text-base font-semibold">{Number(totals.opens ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-gray-500 mt-1">
              {openRate.toFixed(2)}% open rate | {Number(uniqueTotals.unique_opens ?? 0).toLocaleString()} unique
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-gray-400">Clicks</div>
            <div className="text-white text-base font-semibold">{Number(totals.clicks ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-gray-500 mt-1">
              {clickRate.toFixed(2)}% click rate | {Number(uniqueTotals.unique_clicks ?? 0).toLocaleString()} unique
            </div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-3">
            <div className="text-gray-400">Replies</div>
            <div className="text-white text-base font-semibold">{Number(totals.replies ?? 0).toLocaleString()}</div>
            <div className="text-[11px] text-gray-500 mt-1">
              {replyRate.toFixed(2)}% reply rate | {Number(uniqueTotals.unique_replies ?? 0).toLocaleString()} unique
            </div>
          </div>
        </section>

        <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-2">Campaign Performance</div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4">Campaign ID</th>
                <th className="py-2 pr-4">Sends</th>
                <th className="py-2 pr-4">Unique Opens</th>
                <th className="py-2 pr-4">Unique Clicks</th>
                <th className="py-2 pr-4">Unique Replies</th>
                <th className="py-2 pr-4">Bounces</th>
                <th className="py-2 pr-4">Unsubs</th>
              </tr>
            </thead>
            <tbody>
              {topCampaigns.map((row) => (
                <tr key={`${row.campaign_id}-${row.campaign_name || ''}`} className="border-b border-gray-900">
                  <td className="py-2 pr-4 text-gray-200">{row.campaign_name || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{row.campaign_id || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.unique_opens ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.unique_clicks ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.unique_replies ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.unsubscribes ?? 0).toLocaleString()}</td>
                </tr>
              ))}
              {topCampaigns.length === 0 && (
                <tr>
                  <td className="py-4 text-gray-500" colSpan={8}>
                    No campaign analytics found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-2">Sender Performance</div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2 pr-4">Sender</th>
                <th className="py-2 pr-4">Sends</th>
                <th className="py-2 pr-4">Opens</th>
                <th className="py-2 pr-4">Clicks</th>
                <th className="py-2 pr-4">Replies</th>
                <th className="py-2 pr-4">Bounces</th>
              </tr>
            </thead>
            <tbody>
              {topSenders.map((row) => (
                <tr key={`email-data-sender-${row.sender_email}`} className="border-b border-gray-900">
                  <td className="py-2 pr-4 text-gray-200">{row.sender_email || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.opens ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.clicks ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.replies ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.bounces ?? 0).toLocaleString()}</td>
                </tr>
              ))}
              {topSenders.length === 0 && (
                <tr>
                  <td className="py-4 text-gray-500" colSpan={6}>
                    No sender analytics available for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-2">Reply Inbox</div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2 pr-4">Campaign</th>
                <th className="py-2 pr-4">Lead Name</th>
                <th className="py-2 pr-4">Lead Email</th>
                <th className="py-2 pr-4">Sender</th>
                <th className="py-2 pr-4">Replies</th>
                <th className="py-2 pr-4">Interest</th>
                <th className="py-2 pr-4">Last Reply</th>
              </tr>
            </thead>
            <tbody>
              {positiveReplies.map((row) => (
                <tr key={`email-data-reply-${row.reply_key}`} className="border-b border-gray-900">
                  <td className="py-2 pr-4 text-gray-200">{row.campaign_name || row.campaign_id || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{row.lead_name || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{row.lead_email || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{row.sender_email || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.reply_count ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.lt_interest_status ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{row.last_reply_at ? new Date(row.last_reply_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {positiveReplies.length === 0 && (
                <tr>
                  <td className="py-4 text-gray-500" colSpan={7}>
                    No replies available for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        <section className="overflow-x-auto bg-card-dark border border-border-dark rounded-xl p-4">
          <div className="text-xs text-gray-400 mb-2">Daily Activity</div>
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2 pr-4">Day</th>
                <th className="py-2 pr-4">Sends</th>
                <th className="py-2 pr-4">Opens</th>
                <th className="py-2 pr-4">Clicks</th>
                <th className="py-2 pr-4">Replies</th>
              </tr>
            </thead>
            <tbody>
              {dayRows.map((row) => (
                <tr key={`email-data-day-${row.day}`} className="border-b border-gray-900">
                  <td className="py-2 pr-4 text-gray-300">{row.day || '—'}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.sends ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.opens ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.clicks ?? 0).toLocaleString()}</td>
                  <td className="py-2 pr-4 text-gray-300">{Number(row.replies ?? 0).toLocaleString()}</td>
                </tr>
              ))}
              {dayRows.length === 0 && (
                <tr>
                  <td className="py-4 text-gray-500" colSpan={5}>
                    No day-level analytics available for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}
